/**
 * How much of a project's bank the user may use, and the rules that follow
 * from it; the API applies the same rules (lixani-dynamodb-api
 * `project-bank-access.util.ts`, `project-bank-images-scope.util.ts`).
 *
 * - FULL: project bank access (`project_bank_access`): the whole bank, the
 *   bank's own permissions deciding what may be done in it, as before.
 * - GALLERY_ONLY: gallery access without bank access: only the project's
 *   images folder and what is beneath it (the images scope), where the
 *   gallery's permissions decide instead — whose photos they see (the API
 *   leaves out the others'), whether they see who added a photo, whose
 *   photos they may change.
 * - NONE: neither; the bank is not for them.
 * - PENDING: the permissions or the project are not loaded yet, so which of
 *   the three it is cannot be told. Business admins and owners hold both
 *   keys, and telling them apart from a gallery-only user needs the project.
 */
export const BANK_ACCESS_MODES = Object.freeze({
  PENDING: 'PENDING',
  FULL: 'FULL',
  GALLERY_ONLY: 'GALLERY_ONLY',
  NONE: 'NONE',
});

/**
 * Key of a project's images folder: the key the camera files photos under,
 * which the root's `images` folder and its duplicates share. Null for a
 * project without a key.
 * @param {?{business_id?: string, projectKey?: (string|number)}} project
 * @returns {?string}
 */
export const bankImagesFolderKeyOf = (project) => {
  const projectKey = project ? project.projectKey : undefined;
  if (projectKey === undefined || projectKey === null || projectKey === '') {
    return null;
  }
  return project.business_id
    ? `project-bank/${project.business_id}/${projectKey}/images`
    : `project-bank/${projectKey}/images`;
};

/**
 * Key of the images folder under a project's root folder as loaded: the
 * root's key and `images`, which is the key bankImagesFolderKeyOf gives when
 * the project entity is at hand. Null without a root folder.
 * @param {?{key?: string}} rootFolder
 * @returns {?string}
 */
export const bankImagesFolderKeyUnder = (rootFolder) =>
  rootFolder && typeof rootFolder.key === 'string' && rootFolder.key
    ? `${rootFolder.key}/images`
    : null;

const isAtOrUnder = (key, scopeKey) =>
  typeof key === 'string' &&
  (key === scopeKey || key.startsWith(`${scopeKey}/`));

/**
 * Whether a bank row is in the images scope whose folder key is
 * `imagesKey`: a folder at or beneath it, or a file whose folder is (a file
 * without a `parentKey`, a photo saved from a form or the camera, is judged
 * by its own key). Decided by key, never by a folder's name: any folder can
 * be called `images`. Virtual rows (forms, tilaajavastuu) have no such key
 * and are outside.
 * @param {?Object} row raw FilePermission
 * @param {?string} imagesKey
 * @returns {boolean}
 */
export const isInBankImagesScope = (row, imagesKey) => {
  if (!row || !imagesKey || row.deletedAt) return false;
  if (row.type === 'FOLDER') return isAtOrUnder(row.key, imagesKey);
  if (row.parentKey) return isAtOrUnder(row.parentKey, imagesKey);
  return typeof row.key === 'string' && row.key.startsWith(`${imagesKey}/`);
};

/**
 * Whether a folder is the project's images folder itself, the root's
 * `images` or one of its duplicates (they share the key), rather than a
 * folder beneath it.
 * @param {?Object} folder raw folder FilePermission
 * @param {?string} imagesKey
 * @returns {boolean}
 */
export const isBankImagesFolder = (folder, imagesKey) =>
  Boolean(
    folder &&
    imagesKey &&
    folder.type === 'FOLDER' &&
    !folder.deletedAt &&
    folder.key === imagesKey
  );

/**
 * Whether the user may open this folder: any folder with FULL access (and
 * while PENDING — the API decides what there is to open meanwhile), none
 * without access, and only the images scope for a gallery-only user or
 * wherever the bank is kept to it (`imagesOnly`). Open as the project's
 * gallery (`imagesFolderOnly`) the bank stays in the images folder itself:
 * the gallery lists the project's photos from every folder at once, and a
 * link, the assistant or a photo filter that named a folder beneath it would
 * only send new photos there.
 * @param {?Object} folder raw folder FilePermission
 * @param {{mode: string, imagesKey: ?string, imagesOnly?: boolean, imagesFolderOnly?: boolean}} access
 * @returns {boolean}
 */
export const canOpenBankFolder = (
  folder,
  { mode, imagesKey, imagesOnly = false, imagesFolderOnly = false } = {}
) => {
  if (mode === BANK_ACCESS_MODES.NONE) return false;
  if (imagesFolderOnly) return isBankImagesFolder(folder, imagesKey);
  if (imagesOnly || mode === BANK_ACCESS_MODES.GALLERY_ONLY) {
    return isInBankImagesScope(folder, imagesKey);
  }
  return true;
};

/**
 * Whether a bank row is a photo or a video: a file, not a form's mirror,
 * whose mime type is of the `image/` or `video/` kind (a disk image's
 * `application/x-iso9660-image` names an image without being one). The same
 * test as the API's listing of a project's photos and its edit rule for them
 * (lixani-dynamodb-api `isGalleryMediaFile`).
 * @param {?Object} row raw FilePermission
 * @returns {boolean}
 */
export const isBankMediaRow = (row) => {
  if (!row || row.type !== 'FILE' || row.isVirtualProjectBankForm) {
    return false;
  }
  const mimeType = row.file && row.file.mimeType;
  return (
    typeof mimeType === 'string' &&
    (mimeType.startsWith('image/') || mimeType.startsWith('video/'))
  );
};

/**
 * Who put a row in the bank: the bank stamps `user` (joined to the user
 * object in the store), the gallery reads `mirrorUser`; both are the
 * uploader.
 * @param {?Object} row raw FilePermission
 * @returns {?string}
 */
export const bankRowOwnerIdOf = (row) => {
  if (!row) return null;
  const user =
    row.user && typeof row.user === 'object' ? row.user.id : row.user;
  return user || row.mirrorUser || null;
};

/**
 * Whether a gallery-only user may change or delete this photo: the gallery's
 * rule — its uploader, or whoever may edit others' images.
 * @param {?Object} row raw FilePermission
 * @param {{userId: ?string, canEditOthers: boolean}} rule
 * @returns {boolean}
 */
export const canEditBankMediaAsGallery = (row, { userId, canEditOthers }) =>
  Boolean(canEditOthers) ||
  (Boolean(userId) && bankRowOwnerIdOf(row) === userId);

// Whose a row is by the bank's own "own" permissions: the `user` it was
// stamped with, never a `mirrorUser`.
const bankRowUserIdOf = (row) =>
  row.user && typeof row.user === 'object' ? row.user.id : row.user;

const NO_RIGHTS = Object.freeze({ canEdit: false, canDelete: false });
const ALL_RIGHTS = Object.freeze({ canEdit: true, canDelete: true });

/**
 * What the user may do to a public bank row: `canEdit` (rename, describe,
 * rotate, place on a floorplan) and `canDelete`.
 *
 * - A gallery-only user goes by the gallery's rule, on files of the images
 *   scope only (canEditBankMediaAsGallery). The API lists more to a member
 *   whose bank access comes from a project above, but lets them change only
 *   what the gallery reaches.
 * - Everyone else goes by the bank's permissions ("any", or "own" while the
 *   row is theirs), or for a photo or a video by the gallery's rule as well
 *   where they have the gallery: an employee without the bank's edit rights
 *   still changes their own photos, anyone's with "edit other users' images",
 *   as the gallery let them. Documents keep the bank's rules alone. The API
 *   applies the same (lixani-dynamodb-api `galleryEditAccessForBankCaller`).
 * @param {?Object} row raw FilePermission
 * @param {Object} rules hooks/useBankItemRights
 * @param {boolean} [rules.isGalleryOnly]
 * @param {?string} [rules.userId]
 * @param {boolean} [rules.editAny]
 * @param {boolean} [rules.editOwn]
 * @param {boolean} [rules.deleteAny]
 * @param {boolean} [rules.deleteOwn]
 * @param {{userId: ?string, canEditOthers: boolean, hasGalleryAccess?: boolean}} [rules.galleryEditRule]
 * @param {?string} [rules.imagesKey] the images folder's key (selectBankImagesFolderKey)
 * @returns {{canEdit: boolean, canDelete: boolean}}
 */
export const getBankItemRights = (row, rules = {}) => {
  const {
    isGalleryOnly = false,
    userId = null,
    editAny = false,
    editOwn = false,
    deleteAny = false,
    deleteOwn = false,
    galleryEditRule = {},
    imagesKey = null,
  } = rules;
  if (!row) return NO_RIGHTS;
  if (isGalleryOnly) {
    return row.type === 'FILE' &&
      (!imagesKey || isInBankImagesScope(row, imagesKey)) &&
      canEditBankMediaAsGallery(row, galleryEditRule)
      ? ALL_RIGHTS
      : NO_RIGHTS;
  }
  const isOwn = Boolean(userId) && bankRowUserIdOf(row) === userId;
  const byGallery =
    Boolean(galleryEditRule.hasGalleryAccess) &&
    isBankMediaRow(row) &&
    canEditBankMediaAsGallery(row, galleryEditRule);
  return {
    canEdit: Boolean(editAny) || (Boolean(editOwn) && isOwn) || byGallery,
    canDelete: Boolean(deleteAny) || (Boolean(deleteOwn) && isOwn) || byGallery,
  };
};
